import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Send, LogOut, History } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);

  // Notification form
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notificationType, setNotificationType] = useState('announcement');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    // Check if token exists in localStorage
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
      fetchHistory(savedToken);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/notifications/admin/login`, {
        username,
        password
      });

      const newToken = response.data.token;
      setToken(newToken);
      localStorage.setItem('admin_token', newToken);
      setIsLoggedIn(true);
      toast({
        title: "Logged In",
        description: "Welcome to the Admin Panel"
      });
      fetchHistory(newToken);
    } catch (error) {
      toast({
        title: "Login Failed",
        description: error.response?.data?.detail || "Invalid credentials",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setToken('');
    setIsLoggedIn(false);
    localStorage.removeItem('admin_token');
    setUsername('');
    setPassword('');
    toast({
      title: "Logged Out",
      description: "You have been logged out successfully"
    });
  };

  const fetchHistory = async (authToken) => {
    try {
      const response = await axios.get(`${API}/notifications/history`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();

    if (!title || !message) {
      toast({
        title: "Validation Error",
        description: "Please fill in all fields",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/notifications/send`,
        {
          title,
          message,
          notification_type: notificationType,
          target_audience: 'all'
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      toast({
        title: "Notification Sent!",
        description: `Sent to ${response.data.push_sent} subscribers`
      });

      // Clear form
      setTitle('');
      setMessage('');
      setNotificationType('announcement');

      // Refresh history
      fetchHistory(token);
    } catch (error) {
      toast({
        title: "Send Failed",
        description: error.response?.data?.detail || "Failed to send notification",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-600 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Admin Login</h1>
            <p className="text-slate-600">BRACE Dashboard Notifications</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>

          <div className="mt-4 text-center text-xs text-slate-500">
            <p>Default credentials:</p>
            <p>Username: <code className="bg-slate-100 px-1 rounded">admin</code></p>
            <p>Password: <code className="bg-slate-100 px-1 rounded">BraceAdmin2025!</code></p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-rose-50 to-amber-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-800">Admin Panel</h1>
            <p className="text-slate-600">Send notifications to all BRACE Dashboard users</p>
          </div>
          <Button onClick={handleLogout} variant="outline">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Send Notification Form */}
        <Card className="p-6 mb-6">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Send Notification</h2>
          
          <form onSubmit={handleSendNotification} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
              <Input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., New Newsletter Available"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="e.g., The October newsletter is now available. Check it out!"
                rows={4}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
              <Select value={notificationType} onValueChange={setNotificationType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="announcement">Announcement</SelectItem>
                  <SelectItem value="newsletter">Newsletter</SelectItem>
                  <SelectItem value="calendar">Calendar Update</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              <Send className="w-4 h-4 mr-2" />
              {loading ? 'Sending...' : 'Send Notification'}
            </Button>
          </form>
        </Card>

        {/* Notification History */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-slate-800">Notification History</h2>
            <Button 
              onClick={() => setShowHistory(!showHistory)} 
              variant="outline"
              size="sm"
            >
              <History className="w-4 h-4 mr-2" />
              {showHistory ? 'Hide' : 'Show'}
            </Button>
          </div>

          {showHistory && (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {history.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No notifications sent yet</p>
              ) : (
                history.map((notif) => (
                  <div key={notif.id} className="border-l-4 border-teal-500 pl-4 py-2 bg-slate-50 rounded">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-800">{notif.title}</h3>
                        <p className="text-sm text-slate-600">{notif.message}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(notif.created_at).toLocaleString()} • {notif.sent_count} recipients
                        </p>
                      </div>
                      <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded">
                        {notif.notification_type}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminPanel;